import {
  EpicWinConfiguration,
  EpicWinPopupView,
  EpicWinStepName,
  RESOURCES_COMPONENT_TOKEN,
  ResourcesComponent,
  SoundInstance,
} from '@cgs/machines';
import {
  HUD_CONTROLLER_TOKEN,
  TRIFECTA_LAUNCH_PARAMS_TOKEN,
  TrifectaHudController,
  TrifectaLaunchParamsDto,
} from '@cgs/provider-trifecta';
import {
  Action,
  Container,
  easeOutCubicInterpolate,
  EmptyAction,
  FunctionAction,
  InterpolateCopyAction,
  lerp,
  ParallelSimpleAction,
  SceneObject,
  SequenceSimpleAction,
  SoundSceneObject,
  TextSceneObject,
} from '@cgs/wge';

export class SantaEpicWinPopupView extends EpicWinPopupView {
  private readonly currency: string;
  private readonly currencyDecimals: number;
  private readonly trifectaHudController: TrifectaHudController | null;

  private texts: TextSceneObject[];

  private backgroundSoundInstance: SoundInstance;

  constructor(
    container: Container,
    root: SceneObject,
    popup: SceneObject,
    soundNode: SceneObject | null,
    configuration: EpicWinConfiguration
  ) {
    super(container, root, popup, soundNode, configuration);

    const launchParams = container.resolve<TrifectaLaunchParamsDto>(TRIFECTA_LAUNCH_PARAMS_TOKEN);
    const soundSlotNode = container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN).sounds;
    this.currency = launchParams.currencyId;
    this.currencyDecimals = launchParams.currencyDecimals;
    this.trifectaHudController = container.resolve<TrifectaHudController>(HUD_CONTROLLER_TOKEN, {
      optional: true,
    });

    this.texts = this.view.findAllById<TextSceneObject>('result');

    const backgroundSound = soundSlotNode.findById<SoundSceneObject>('background_win_popups')!;

    this.backgroundSoundInstance = new SoundInstance(
      backgroundSound,
      backgroundSound.stateMachine!
    );

    this.showing.listen(() => {
      this.backgroundSoundInstance.GoToState('show');
    });
  }

  public setWinAmount(winAmount: number): void {
    const finalWinAmount = this.numberFormatter.format(winAmount, this.currencyDecimals);

    this.texts.forEach((text) => {
      text.text = `${this.currency} ${finalWinAmount}`;
      text.format = '';
    });
  }

  public resetShare(): void {}

  protected override getTotalBet(): number {
    if (this.trifectaHudController) {
      return this.trifectaHudController.getTotalBet();
    }
    return this._slotSession.totalBet;
  }

  public hide(): void {
    this.backgroundSoundInstance.GoToState('hide');
    this._popupNode.stateMachine?.switchToState('hide');

    super.hide();
  }

  protected interpolateTextAction(startValue: number, totalWin: number): Action {
    const start0 = startValue;
    const start1 = 0.1 * (totalWin - startValue) + startValue;
    const start2 = 0.85 * (totalWin - startValue) + startValue;
    const firstAction = new InterpolateCopyAction<number>().withInterpolateFunction(lerp);
    firstAction.withDuration(0.8).withValues(start0, start1);
    firstAction.valueChange.listen((win) => {
      this.setWinAmount(win);
    });
    firstAction.beginning.listen(() => {
      this._soundNode?.stateMachine?.switchToState('default');
      this._soundNode?.stateMachine?.switchToState('bigwin');
    });

    const secondAction = new InterpolateCopyAction<number>().withInterpolateFunction(lerp);
    secondAction.withDuration(1.0).withValues(start1, start2);
    secondAction.valueChange.listen((win) => {
      this.setWinAmount(win);
    });
    secondAction.beginning.listen(() => {
      this._soundNode?.stateMachine?.switchToState('default');
      this._soundNode?.stateMachine?.switchToState('megawin');
    });
    const thirdAction = new InterpolateCopyAction<number>().withInterpolateFunction(
      easeOutCubicInterpolate
    );
    thirdAction.withDuration(0.8).withValues(start2, totalWin);
    thirdAction.valueChange.listen((win) => {
      this.setWinAmount(win);
    });
    thirdAction.done.listen(() => {
      this._cashLoopSoundScene?.stateMachine?.switchToState('stop');
    });
    thirdAction.beginning.listen(() => {
      this._soundNode?.stateMachine?.switchToState('default');
      this._soundNode?.stateMachine?.switchToState('epicwin');
    });
    return new SequenceSimpleAction([firstAction, secondAction, thirdAction]);
  }

  public showWinAction(winName: EpicWinStepName, totalWin: number): Action {
    this._popupNode.touchable = true;
    this._closeBtn.touchable = true;

    const epicWinStep = this._configuration.steps.find((s) => s.winName == winName)!;

    const animationActions = new Array<Action>();
    this._currentStep = 0;
    this.addActions(epicWinStep, winName, animationActions, totalWin);

    animationActions.push(
      new ParallelSimpleAction([
        new FunctionAction(() => this.endTextAnimation()),
        new EmptyAction().withDuration(
          this._configuration.finishTextAnimationDelay + this._configuration.showCounterDelay
        ),
      ])
    );

    animationActions.push(
      new ParallelSimpleAction([new FunctionAction(() => this.hideWin(winName))])
    );
    animationActions.push(new FunctionAction(() => new Promise(() => this.hide())));

    const sequence = new SequenceSimpleAction(animationActions);

    return new ParallelSimpleAction([sequence]);
  }
}
